import { useEffect, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { CompanyRole, PermissionKey } from "@ligament/shared";
import { usePractice } from "../context/PracticeContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { useConfirm } from "../context/ConfirmContext";
import { useToast } from "../context/ToastContext";
import { accessApi } from "../api/access";
import { companiesApi, type CompanyMember } from "../api/companies";
import { PageSkeleton } from "../components/PageSkeleton";
import { EmptyState } from "../components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "../lib/utils";
import {
  Shield,
  Plus,
  Search,
  ChevronRight,
  Lock,
  Pencil,
  Trash2,
  Loader2,
  Check,
  UserPlus,
} from "lucide-react";

// ── Permission catalog presentation ───────────────────────────────
// Drive everything off the real PERMISSION_KEYS (fetched from the API). Group
// keys by their leading namespace (the segment before the first ":"); tool keys
// (tools:<domain>:<verb>) group under "tools".
const GROUP_LABELS: Record<string, string> = {
  agents: "Agents",
  users: "Users",
  tasks: "Tasks",
  joins: "Joins",
  tools: "Tools",
  workflows: "Workflows",
};

function groupKey(key: string): string {
  return key.split(":")[0] ?? key;
}

/** Humanize the part of the key after the group prefix, e.g. "ehr:read" → "EHR read". */
function permLabel(key: string): string {
  const rest = key.split(":").slice(1).join(":");
  return rest
    .split(/[:_]/)
    .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

function groupPermissions(keys: readonly string[]) {
  const groups = new Map<string, string[]>();
  for (const key of keys) {
    const g = groupKey(key);
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(key);
  }
  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, perms]) => ({
      group,
      label: GROUP_LABELS[group] ?? group.charAt(0).toUpperCase() + group.slice(1),
      perms: perms.sort(),
    }));
}

interface RoleFormState {
  name: string;
  description: string;
  permissionKeys: Set<string>;
}

const EMPTY_FORM: RoleFormState = { name: "", description: "", permissionKeys: new Set() };

export function AdminRoles() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const queryClient = useQueryClient();
  const { selectedPracticeId } = usePractice();
  const confirm = useConfirm();
  const { pushToast } = useToast();

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<CompanyRole | null>(null);
  const [form, setForm] = useState<RoleFormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Assign-role dialog ────────────────────────────────────────
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedPrincipalId, setSelectedPrincipalId] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  useEffect(() => {
    setBreadcrumbs([{ label: "Administration" }, { label: "Roles" }]);
  }, [setBreadcrumbs]);

  const rolesKey = ["access", "roles", selectedPracticeId] as const;
  const permsKey = ["access", "permission-keys", selectedPracticeId] as const;

  const { data: roles, isLoading } = useQuery({
    queryKey: rolesKey,
    queryFn: () => accessApi.listRoles(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: permData } = useQuery({
    queryKey: permsKey,
    queryFn: () => accessApi.listPermissionKeys(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const permissionKeys = permData?.permissionKeys ?? [];
  const grouped = useMemo(() => groupPermissions(permissionKeys), [permissionKeys]);

  const activeRole = useMemo(
    () => roles?.find((r) => r.id === selectedRoleId) ?? null,
    [roles, selectedRoleId],
  );

  const createMutation = useMutation({
    mutationFn: (input: { name: string; description: string | null; permissionKeys: PermissionKey[] }) =>
      accessApi.createRole(selectedPracticeId!, input),
    onSuccess: (role) => {
      queryClient.invalidateQueries({ queryKey: rolesKey });
      setSelectedRoleId(role.id);
      setDialogOpen(false);
    },
    onError: (e: unknown) => setFormError(e instanceof Error ? e.message : String(e)),
  });

  const updateMutation = useMutation({
    mutationFn: (args: {
      roleId: string;
      input: { name?: string; description?: string | null; permissionKeys?: PermissionKey[] };
    }) => accessApi.updateRole(selectedPracticeId!, args.roleId, args.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesKey });
      setDialogOpen(false);
    },
    onError: (e: unknown) => setFormError(e instanceof Error ? e.message : String(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (roleId: string) => accessApi.deleteRole(selectedPracticeId!, roleId),
    onSuccess: (_res, roleId) => {
      queryClient.invalidateQueries({ queryKey: rolesKey });
      if (selectedRoleId === roleId) setSelectedRoleId(null);
    },
  });

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["companies", "members", selectedPracticeId],
    queryFn: () => companiesApi.members(selectedPracticeId!),
    enabled: !!selectedPracticeId && assignOpen,
  });

  const assignMutation = useMutation({
    mutationFn: (member: CompanyMember) =>
      accessApi.assignRole(selectedPracticeId!, activeRole!.id, {
        principalType: "user",
        principalId: member.principalId,
      }),
    onSuccess: (_res, member) => {
      pushToast({
        tone: "success",
        title: `Assigned ${activeRole?.name ?? "role"} to ${member.name ?? member.email}`,
      });
      setAssignOpen(false);
    },
    onError: (e: unknown) => setAssignError(e instanceof Error ? e.message : String(e)),
  });

  function openAssign() {
    setSelectedPrincipalId(null);
    setAssignError(null);
    assignMutation.reset();
    setAssignOpen(true);
  }

  function openCreate() {
    setEditingRole(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setDialogOpen(true);
  }

  function openEdit(role: CompanyRole) {
    setEditingRole(role);
    setForm({
      name: role.name,
      description: role.description ?? "",
      permissionKeys: new Set(role.permissionKeys),
    });
    setFormError(null);
    setDialogOpen(true);
  }

  function togglePerm(key: string) {
    setForm((prev) => {
      const next = new Set(prev.permissionKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, permissionKeys: next };
    });
  }

  function submitForm() {
    setFormError(null);
    if (!form.name.trim()) {
      setFormError("Role name is required");
      return;
    }
    const keys = Array.from(form.permissionKeys) as PermissionKey[];
    if (editingRole) {
      updateMutation.mutate({
        roleId: editingRole.id,
        input: {
          name: form.name.trim(),
          description: form.description.trim() || null,
          permissionKeys: keys,
        },
      });
    } else {
      createMutation.mutate({
        name: form.name.trim(),
        description: form.description.trim() || null,
        permissionKeys: keys,
      });
    }
  }

  if (!selectedPracticeId) {
    return (
      <div className="p-6">
        <EmptyState icon={Shield} message="Select a practice to manage its roles." />
      </div>
    );
  }
  if (isLoading) return <PageSkeleton />;

  const filteredRoles = (roles ?? []).filter(
    (r) => !searchQuery || r.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-foreground">Roles &amp; Permissions</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Define reusable permission templates. Assigning a role grants its permissions to a
              member or agent.
            </p>
          </div>
          <Button size="sm" className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            Create Role
          </Button>
        </div>

        <div className="grid grid-cols-[320px_1fr] gap-6">
          {/* Role list */}
          <div className="space-y-2">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {filteredRoles.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No roles yet. Create one to get started.
              </div>
            ) : (
              filteredRoles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => setSelectedRoleId(role.id)}
                  className={cn(
                    "w-full text-left rounded-lg border p-3 transition-colors",
                    selectedRoleId === role.id
                      ? "border-primary bg-primary/5"
                      : "border-border bg-card hover:border-primary/30",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield
                        className={cn(
                          "h-4 w-4",
                          selectedRoleId === role.id ? "text-primary" : "text-muted-foreground",
                        )}
                      />
                      <span className="text-sm font-medium text-foreground">{role.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {role.isSystem && <Lock className="h-3 w-3 text-muted-foreground" />}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  {role.description && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {role.description}
                    </p>
                  )}
                  <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                    <span>
                      {role.permissionKeys.length} permission
                      {role.permissionKeys.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Permission detail panel */}
          <div className="rounded-lg border border-border bg-card">
            {activeRole ? (
              <div>
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-foreground">{activeRole.name}</h2>
                      {activeRole.isSystem && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted text-muted-foreground">
                          <Lock className="h-2.5 w-2.5" />
                          System
                        </span>
                      )}
                    </div>
                    {activeRole.description && (
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {activeRole.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={openAssign}
                    >
                      <UserPlus className="h-3.5 w-3.5" />
                      Assign
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => openEdit(activeRole)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    {!activeRole.isSystem && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-destructive hover:text-destructive"
                        onClick={async () => {
                          if (
                            await confirm({
                              title: `Delete the role "${activeRole.name}"?`,
                              description:
                                "This does not revoke permissions already assigned to members.",
                              destructive: true,
                            })
                          ) {
                            deleteMutation.mutate(activeRole.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>

                {/* Permissions grid (read-only, grouped by namespace) */}
                <div className="px-6 py-4 space-y-6">
                  {grouped.map(({ group, label, perms }) => (
                    <div key={group}>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
                        {label}
                      </h3>
                      <div className="space-y-1.5">
                        {perms.map((key) => {
                          const has = activeRole.permissionKeys.includes(key as PermissionKey);
                          return (
                            <div
                              key={key}
                              className="flex items-center justify-between py-1.5 px-3 rounded-md bg-muted/30"
                            >
                              <div>
                                <p className="text-sm font-medium text-foreground">
                                  {permLabel(key)}
                                </p>
                                <p className="text-xs font-mono text-muted-foreground">{key}</p>
                              </div>
                              <div
                                className={cn(
                                  "flex items-center gap-1.5 text-xs font-medium",
                                  has ? "text-emerald-500" : "text-muted-foreground",
                                )}
                              >
                                {has ? (
                                  <>
                                    <Check className="h-3.5 w-3.5" />
                                    Allowed
                                  </>
                                ) : (
                                  <>
                                    <Lock className="h-3.5 w-3.5" />
                                    Denied
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-96 text-muted-foreground text-sm">
                Select a role to view its permissions
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? "Edit Role" : "Create Role"}</DialogTitle>
            <DialogDescription>
              Choose the permissions this role grants. Members or agents assigned this role receive
              exactly these permissions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label htmlFor="role-name" className="text-xs font-semibold text-muted-foreground">
                Name
              </label>
              <Input
                id="role-name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Practice Administrator"
                className="mt-1"
              />
            </div>
            <div>
              <label htmlFor="role-desc" className="text-xs font-semibold text-muted-foreground">
                Description
              </label>
              <Textarea
                id="role-desc"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="What this role is for"
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <span className="text-xs font-semibold text-muted-foreground">Permissions</span>
              <div className="mt-2 space-y-4">
                {grouped.map(({ group, label, perms }) => (
                  <div key={group}>
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      {label}
                    </h4>
                    <div className="space-y-1.5">
                      {perms.map((key) => (
                        <label
                          key={key}
                          className="flex items-center gap-3 py-1.5 px-3 rounded-md bg-muted/30 cursor-pointer hover:bg-accent/50"
                        >
                          <Checkbox
                            checked={form.permissionKeys.has(key)}
                            onCheckedChange={() => togglePerm(key)}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{permLabel(key)}</p>
                            <p className="text-xs font-mono text-muted-foreground">{key}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={submitForm} disabled={saving}>
              {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {editingRole ? "Save Changes" : "Create Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign role dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign {activeRole?.name ?? "Role"}</DialogTitle>
            <DialogDescription>
              Choose a member to grant this role. Assigning expands this role's permissions onto the
              member.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {membersLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading members…
              </div>
            ) : (members ?? []).filter((m) => m.principalType === "user").length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No members in this practice yet.
              </div>
            ) : (
              <div className="space-y-1.5">
                {(members ?? [])
                  .filter((m) => m.principalType === "user")
                  .map((member) => {
                    const selected = selectedPrincipalId === member.principalId;
                    return (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => setSelectedPrincipalId(member.principalId)}
                        className={cn(
                          "w-full text-left flex items-center justify-between gap-3 rounded-md border px-3 py-2 transition-colors",
                          selected
                            ? "border-primary bg-primary/5"
                            : "border-border bg-card hover:border-primary/30",
                        )}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {member.name ?? member.email}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                        </div>
                        <span className="shrink-0 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span className="px-2 py-0.5 rounded-full bg-muted">{member.role}</span>
                          {selected && <Check className="h-4 w-4 text-primary" />}
                        </span>
                      </button>
                    );
                  })}
              </div>
            )}

            {assignError && <p className="text-sm text-destructive">{assignError}</p>}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAssignOpen(false)}
              disabled={assignMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!selectedPrincipalId || assignMutation.isPending}
              onClick={() => {
                const member = (members ?? []).find((m) => m.principalId === selectedPrincipalId);
                if (member) assignMutation.mutate(member);
              }}
            >
              {assignMutation.isPending && (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              )}
              Assign role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
